// Multi-org support for the employee surface.
//
// A user can belong to more than one pulse org (as an employee, an admin, or
// both). Everything used to resolve through `my_pulse_org_id()` which was an
// unordered LIMIT 1, so a second membership was invisible. This module is the
// single source of truth for "which orgs do I belong to" and "which one am I
// looking at right now".
import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export type PulseOrg = {
  org_id: string;
  name: string;
  role: string | null;
  has_employee: boolean;
  joined_at: string;
};

const ACTIVE_KEY = 'pulse:activeOrgId';
const listeners = new Set<() => void>();

export function getStoredActiveOrg(): string | null {
  try { return localStorage.getItem(ACTIVE_KEY); } catch { return null; }
}

export function setStoredActiveOrg(orgId: string | null) {
  try {
    if (orgId) localStorage.setItem(ACTIVE_KEY, orgId);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch { /* ignore */ }
  listeners.forEach((l) => { try { l(); } catch { /* ignore */ } });
}

export function useMyOrgs() {
  const { user, isLoading: authLoading } = useAuth();
  return useQuery({
    queryKey: ['my-pulse-orgs', user?.id],
    enabled: !authLoading && !!user?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<PulseOrg[]> => {
      const { data, error } = await supabase.rpc('my_pulse_orgs');
      if (error) throw error;
      return (data ?? []) as PulseOrg[];
    },
  });
}

/** Active org id, kept in sync with localStorage across components. */
export function useActiveOrgId(orgs: PulseOrg[] | undefined): string | null {
  const [, force] = useState(0);

  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);

  if (!orgs || orgs.length === 0) return null;
  const stored = getStoredActiveOrg();
  if (stored && orgs.some((o) => o.org_id === stored)) return stored;
  return orgs[0].org_id;
}

/** Switch org and drop every org-scoped cache. */
export function useSwitchOrg() {
  const qc = useQueryClient();
  return useCallback((orgId: string) => {
    setStoredActiveOrg(orgId);
    qc.invalidateQueries();
  }, [qc]);
}
